import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Eye, EyeOff } from 'react-feather';
import './App.css';
import PropertyAdminDashboard from './components/admin/PropertyAdminDashboard';
import EditPropertyPage from './components/admin/EditPropertyPage';
import AgentPortal from './components/agent/AgentPortal';
import AgentSignup from './components/agent/AgentSignup';
import AgentOnboarding from './components/agent/AgentOnboarding';
import AuthService from './services/authService';
import logo from './assets/LogoWhiteCropped.png';
import DenverPreview from './components/agent/AgentPreviews/DenverPreview';
import DenverTrial from './components/agent/AgentTrials/DenverTrial';
import Unsubscribe from './components/agent/Unsubscribe';


// Docs imports
import DocsLayout from './components/docs/DocsLayout';
import DocsHome from './components/docs/DocsHome';
import DocsArticle from './components/docs/DocsArticle';

// PWA components — install prompt and update banner. Both are self-contained
// and decide internally when to render based on browser/install state.
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAUpdateBanner from './components/PWAUpdateBanner';



function App() {
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Login state variables
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  // Current view state
  const [currentView, setCurrentView] = useState('login'); // 'login', 'reset-email', 'reset-code', 'reset-password'

  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  useEffect(() => {
    // Check user role on initial load
    checkUserRole();
  }, []);

  const checkUserRole = () => {
    const user = AuthService.getCurrentUser();
    setUserRole(user ? user.role : null);
    setIsLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoginLoading(true);
    
    try {
      // Call your authentication service
      const data = await AuthService.login(email.trim().toLowerCase(), password);
      
      // Check if login was successful
      if (data && data.success) {
        // Update user role which will trigger redirect
        const user = AuthService.getCurrentUser();
        setUserRole(user.role);
      } else {
        // Handle login failure
        setLoginError('Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(error.message || 'An error occurred during login');
    } finally {
      setIsLoginLoading(false);
    }
  };

  const startPasswordReset = (e) => {
    e.preventDefault();
    setResetEmail(email); // Pre-fill with login email if available
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setResetError('');
    setResetSuccess('');
    setCurrentView('reset-email');
  };

  const backToLogin = () => {
    setCurrentView('login');
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setResetError('');
    setResetSuccess('');
  };

  // Step 1: Send reset email
  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError('');
    setResetSuccess('');

    if (!resetEmail) {
      setResetError('Please enter your email address');
      setIsResetting(false);
      return;
    }

    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail.trim().toLowerCase() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      setResetSuccess('Reset code sent to your email!');
      setTimeout(() => {
        setCurrentView('reset-code');
        setResetSuccess('');
      }, 1500);

    } catch (error) {
      setResetError(error.message || 'An error occurred while sending reset email');
    } finally {
      setIsResetting(false);
    }
  };

  // Step 2: Verify reset code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError('');

    if (!resetCode || resetCode.length !== 6) {
      setResetError('Please enter the 6-digit code');
      setIsResetting(false);
      return;
    }

    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/auth/verify-reset-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: resetEmail.trim().toLowerCase(),
          code: resetCode 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid or expired code');
      }

      setResetSuccess('Code verified! Set your new password.');
      setTimeout(() => {
        setCurrentView('reset-password');
        setResetSuccess('');
      }, 1500);

    } catch (error) {
      setResetError(error.message || 'Invalid or expired code');
    } finally {
      setIsResetting(false);
    }
  };

  // Step 3: Set new password
  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError('');

    if (!newPassword || newPassword.length < 6) {
      setResetError('Password must be at least 6 characters long');
      setIsResetting(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      setIsResetting(false);
      return;
    }

    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: resetEmail.trim().toLowerCase(),
          code: resetCode,
          newPassword: newPassword 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setResetSuccess('Password reset successfully! You can now log in.');
      setTimeout(() => {
        backToLogin();
      }, 2500);

    } catch (error) {
      setResetError(error.message || 'An error occurred while resetting password');
    } finally {
      setIsResetting(false);
    }
  };

  // Handle successful agent signup (auto-login)
  const handleSignupSuccess = (user) => {
    setUserRole(user.role);
    // Force navigation to agent portal - full reload ensures all auth state is fresh
    window.location.href = '/agent';
  };

  // Agent finished onboarding step 1 (set password). AuthService already stored
  // the JWT the backend handed back, so they're authenticated — same full-reload
  // handoff as signup.
  const handleOnboardingComplete = (user) => {
    setUserRole(user.role);
    window.location.href = '/agent';
  };

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Docs Routes - Public, no auth required */}
          <Route path="/docs" element={
            <DocsLayout>
              <DocsHome />
            </DocsLayout>
          } />
          <Route path="/docs/:category/:article" element={
            <DocsLayout>
              <DocsArticle />
            </DocsLayout>
          } />

          {/* Agent onboarding - public, no auth required.
              The agent arrives here from the link we email at account creation
              and has never logged in, so this must NOT be gated on userRole. */}
          <Route
            path="/onboarding/:token"
            element={<AgentOnboarding onComplete={handleOnboardingComplete} />}
          />

          <Route path="/admin" element={<PropertyAdminDashboard />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/admin/properties/edit/:id" element={<EditPropertyPage />} />
          <Route path="/agent" element={
  userRole === 'agent' ? <AgentPortal /> : <Navigate to="/" replace />
} />
          <Route 
            path="/trial-start" 
            element={
              userRole ? (
                userRole === 'admin' ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/agent" replace />
                )
              ) : (
                <AgentSignup onSignupSuccess={handleSignupSuccess} />
              )
            } 
          />
                    {/* Public preview pages for ads */}
                    <Route path="/denver-preview" element={<DenverPreview />} />
                    <Route path="/denver-trial" element={<DenverTrial />} />
          <Route path="/" element={
            userRole === 'admin' ? (
              <Navigate to="/admin" replace />
            ) : userRole === 'agent' ? (
              <Navigate to="/agent" replace />
            ) : (
              <div className="login-page">
                <div className="login-container">
                  <div className="login-form-container">
                    <div className="login-logo">
                      <img src={logo} alt="Logo" className="company-logo" />
                    </div>

                    {/* LOGIN VIEW */}
                    {currentView === 'login' && (
                      <div className="login-form">
                        {loginError && (
                          <div className="login-error">
                            {loginError}
                          </div>
                        )}
                        <form onSubmit={handleLogin}>
                          <input 
                            type="email" 
                            className="login-input" 
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)} 
                            required
                          />
                          <div className="password-input-container">
                            <input 
                              type={showPassword ? "text" : "password"} 
                              className="login-input" 
                              placeholder="Password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)} 
                              required
                              style={{ paddingRight: '45px', background: 'white', color: '#333' }}
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => setShowPassword(!showPassword)}
                              tabIndex={-1}
                            >
                              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                          <div className="remember-me">
                            <input type="checkbox" id="remember" />
                            <label htmlFor="remember">Remember Me</label>
                            <a href="#" className="trouble-link" onClick={startPasswordReset}>Reset Password</a>
                          </div>
                          <button 
                            type="submit" 
                            className="login-button"
                            disabled={isLoginLoading}
                          >
                            {isLoginLoading ? 'Logging in...' : 'Login'}
                          </button>
                        </form>
                      </div>
                    )}

                    {/* RESET EMAIL VIEW */}
                    {currentView === 'reset-email' && (
                      <div className="reset-form">
                        <h2 className="reset-title">Reset Password</h2>
                        <p className="reset-subtitle">Enter your email address and we'll send you a verification code.</p>
                        
                        {resetError && (
                          <div className="reset-error">
                            {resetError}
                          </div>
                        )}

                        {resetSuccess && (
                          <div className="reset-success">
                            {resetSuccess}
                          </div>
                        )}

                        <form onSubmit={handleSendResetEmail}>
                          <input
                            type="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            className="login-input"
                            placeholder="your.email@example.com"
                            required
                          />
                          
                          <div className="reset-actions">
                          <button
  type="button"
  className="back-button"
  onClick={backToLogin}
  disabled={isResetting}
>
Back
</button>
                            <button
                              type="submit"
                              className="login-button"
                              disabled={isResetting}
                            >
                              {isResetting ? 'Sending...' : 'Send Code'}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {/* RESET CODE VIEW */}
                    {currentView === 'reset-code' && (
                      <div className="reset-form">
                        <h2 className="reset-title">Enter Verification Code</h2>
                        <p className="reset-subtitle">Enter the 6-digit code sent to {resetEmail}</p>
                        
                        {resetError && (
                          <div className="reset-error">
                            {resetError}
                          </div>
                        )}

                        {resetSuccess && (
                          <div className="reset-success">
                            {resetSuccess}
                          </div>
                        )}

                        <form onSubmit={handleVerifyCode}>
                          <input
                            type="text"
                            value={resetCode}
                            onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="code-input"
                            placeholder="000000"
                            maxLength="6"
                            required
                          />
                          
                          <div className="reset-actions">
                            <button
                              type="button"
                              className="back-button"
                              onClick={() => setCurrentView('reset-email')}
                              disabled={isResetting}
                            >
                              Back
                            </button>
                            <button
                              type="submit"
                              className="login-button"
                              disabled={isResetting || resetCode.length !== 6}
                            >
                              {isResetting ? 'Verifying...' : 'Verify Code'}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {/* RESET PASSWORD VIEW */}
                    {currentView === 'reset-password' && (
                      <div className="reset-form">
                        <h2 className="reset-title">Set New Password</h2>
                        <p className="reset-subtitle">Choose a strong password for your account.</p>
                        
                        {resetError && (
                          <div className="reset-error">
                            {resetError}
                          </div>
                        )}

                        {resetSuccess && (
                          <div className="reset-success">
                            {resetSuccess}
                          </div>
                        )}

                        <form onSubmit={handleSetNewPassword}>
                          <div className="password-input-container">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="login-input"
                              placeholder="Enter new password"
                              minLength="6"
                              required
                              style={{ paddingRight: '45px', background: 'white', color: '#333' }}
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              tabIndex={-1}
                            >
                              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                          
                          <div className="password-input-container">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className="login-input"
                              placeholder="Confirm new password"
                              minLength="6"
                              required
                              style={{ paddingRight: '45px', background: 'white', color: '#333' }}
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              tabIndex={-1}
                            >
                              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                          
                          <div className="reset-actions">
                            <button
                              type="button"
                              className="back-button"
                              onClick={() => setCurrentView('reset-code')}
                              disabled={isResetting}
                            >
                              Back
                            </button>
                            <button
                              type="submit"
                              className="login-button"
                              disabled={isResetting}
                            >
                              {isResetting ? 'Resetting...' : 'Reset Password'}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    <div className="admin-link">
                      <a href="https://calendly.com/team-lauft/demo" target="_blank" rel="noopener noreferrer">Book a Demo</a>
                    </div>

                    <div className="disclaimer-text">
                      Important: To be listed on Lauft, a Purchase/Sale listing must be actively listed by a licensed real estate professional in their local Multiple Listing Service (MLS).
                    </div>

                  </div>
                </div>
                <div className="login-image"></div>

                {/* Additional CSS for reset forms */}
                <style jsx>{`
                  .password-input-container {
                    position: relative;
                    width: 100%;
                  }
                  
                  .password-toggle-btn {
                    position: absolute;
                    right: 12px;
                    top: 40%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: rgba(0, 0, 0, 0.5);
                    transition: color 0.2s;
                  }
                  
                  .password-toggle-btn:hover {
                    color: rgba(0, 0, 0, 0.8);
                  }
                  
                  .reset-form {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    animation: slideIn 0.3s ease-out;
                  }

                  .reset-title {
                    color: #FFFFFF;
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 0.5rem;
                    text-align: center;
                    font-family: 'Montserrat', 'Roboto', sans-serif;
                  }

                  .reset-subtitle {
                    color: rgba(255, 255, 255, 0.8);
                    font-size: 0.9rem;
                    margin-bottom: 2rem;
                    text-align: center;
                    line-height: 1.4;
                  }

                  .reset-actions {
                    display: flex;
                    gap: 12px;
                    margin-top: 1rem;
                  }

                  .back-button {
                    background: rgba(255, 255, 255, 0.1);
                    color: #FFFFFF;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    padding: 0.8rem 1.5rem;
                    border-radius: 4px;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    font-family: 'Montserrat', 'Roboto', sans-serif;
                    font-weight: 500;
                    flex: 1;
                  }

                  .back-button:hover {
                    background: rgba(255, 255, 255, 0.2);
                    transform: translateY(-1px);
                  }

                  .back-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                  }

                  .code-input {
                    text-align: center;
                    font-size: 1.8rem;
                    letter-spacing: 8px;
                    font-family: 'Courier New', monospace;
                    font-weight: bold;
                    padding: 1rem;
                    margin-bottom: 1rem;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    width: 100%;
                    background: rgba(255, 255, 255, 0.95);
                  }

                  .reset-error {
                    background-color: rgba(220, 53, 69, 0.1);
                    border-left: 3px solid #dc3545;
                    color: #ff6b6b;
                    padding: 12px 16px;
                    margin-bottom: 15px;
                    font-size: 0.9rem;
                    border-radius: 4px;
                  }

                  .reset-success {
                    background-color: rgba(40, 167, 69, 0.1);
                    border-left: 3px solid #28a745;
                    color: #51cf66;
                    padding: 12px 16px;
                    margin-bottom: 15px;
                    font-size: 0.9rem;
                    border-radius: 4px;
                  }

                  @keyframes slideIn {
                    from {
                      opacity: 0;
                      transform: translateX(-20px);
                    }
                    to {
                      opacity: 1;
                      transform: translateX(0);
                    }
                  }

                  /* Mobile responsiveness */
                  @media (max-width: 768px) {
                    .reset-actions {
                      flex-direction: column;
                    }
                    
                    .back-button {
                      flex: none;
                    }
                    
                    .reset-title {
                      font-size: 1.3rem;
                    }
                    
                    .code-input {
                      font-size: 1.5rem;
                      letter-spacing: 4px;
                    }
                  }
                `}</style>
              </div>
            )
          } />
        </Routes>

        {/* PWA prompts — render globally, regardless of route. They self-hide
            when not applicable (e.g., already installed, dismissed, etc.) */}
        <PWAInstallPrompt />
        <PWAUpdateBanner />
      </div>
    </Router>
  );
}

export default App;